import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {{ id: number, name: string, price: number }} SellingPlan
 * @typedef {{ id: number, title: string, price: number, compare: number, available: boolean, requiresPlan: boolean, plans: SellingPlan[] }} SetItem
 * @typedef {SetItem & { quantity: number, role: string, stepTitle: string, subscribable: boolean }} SetLine
 *
 * Step roles (Bundle item → "Role in the set"):
 *   set   — main set (required for the free pouch)
 *   free  — free pouch: price 0 with a set (1 unit)
 *   addon — extra pouches: their number sets the % off the set and the add-ons (Matcha rewards metaobject)
 *   none  — added to the cart, no bundle discount
 */

const ADDED_STATE_DURATION = 2000;

class SetBuilder extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {WeakMap<Element, Record<string, SetItem>>} */
  #itemsCache = new WeakMap();

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);

    // Steps / rewards are re-rendered, added or removed independently in the theme editor
    this.#observer = new MutationObserver(() => this.#refresh());
    this.querySelectorAll('.set-builder__steps, [data-set-rewards-unlocked], [data-set-rewards-next]').forEach((container) => {
      this.#observer?.observe(container, { childList: true });
    });

    // Rewards config (matcha-rewards.js) loads as a separate module
    document.addEventListener('matcha-rewards:ready', this.#handleRewardsReady);

    this.#refresh();
  }

  #handleRewardsReady = () => this.#update();

  /** Rewards program of this builder — written to the `_bundle` line property. */
  get #program() {
    return this.dataset.program || 'matcha-set-builder';
  }

  /** Bundle % for the number of add-on pouches (tiers from the Matcha rewards metaobject). */
  #bundlePercent(addonCount) {
    const rewards = /** @type {any} */ (window).MatchaRewards;
    if (!rewards || addonCount <= 0) return 0;
    return Number(rewards.percentFor(this.#program, addonCount)) || 0;
  }

  /**
   * Prices exactly like the discount function: % from the original price on set + add-ons,
   * free pouch = 0 for one unit when a set is selected; free pouch is always one-time.
   * @param {SetLine[]} lines
   * @param {string} frequency
   */
  #pricing(lines, frequency) {
    const hasSet = lines.some((line) => line.role === 'set');
    const addonCount = lines.filter((line) => line.role === 'addon').reduce((sum, line) => sum + line.quantity, 0);
    const percent = this.#bundlePercent(addonCount);

    let original = 0;
    let onetime = 0;
    let subscription = 0;
    let recurring = 0;

    for (const line of lines) {
      const lineOriginal = line.price * line.quantity;
      original += lineOriginal;

      if (line.role === 'free' && hasSet) {
        const paidUnits = Math.max(0, line.quantity - 1);
        onetime += line.price * paidUnits;
        subscription += line.price * paidUnits;
        continue;
      }

      const discount = line.role === 'set' || line.role === 'addon' ? Math.round((lineOriginal * percent) / 100) : 0;
      const plan = this.#planForLine(line, frequency, true);
      onetime += lineOriginal - discount;
      subscription += Math.max(0, (plan ? plan.price : line.price) * line.quantity - discount);
      // Renewal = only the products that get a selling plan, at the selling plan price
      if (plan) recurring += plan.price * line.quantity;
    }

    return { hasSet, addonCount, percent, original, onetime, subscription, recurring };
  }

  disconnectedCallback() {
    document.removeEventListener('matcha-rewards:ready', this.#handleRewardsReady);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.#observer?.disconnect();
  }

  /** @returns {HTMLElement[]} */
  get #steps() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-set-step]')]);
  }

  /** @param {HTMLElement} step @returns {Record<string, SetItem>} */
  #getItems(step) {
    const script = step.querySelector('script[data-step-items]');
    if (!script) return {};
    if (this.#itemsCache.has(script)) return /** @type {Record<string, SetItem>} */ (this.#itemsCache.get(script));

    let items = {};
    try {
      items = JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('[set-builder] Invalid step data', error);
    }
    this.#itemsCache.set(script, items);
    return items;
  }

  /** @param {HTMLElement} step @returns {SetLine[]} */
  #getStepLines(step) {
    const items = this.#getItems(step);
    const role = step.dataset.role || 'none';
    const stepTitle = step.dataset.title || '';
    const subscribable = this.#isSubscriptionStep(step);
    /** @type {SetLine[]} */
    const lines = [];

    if (step.dataset.mode === 'list') {
      step.querySelectorAll('[data-set-row]').forEach((row) => {
        if (!(row instanceof HTMLElement)) return;
        const quantity = Number(row.dataset.qty) || 0;
        const item = items[row.dataset.variantId || ''];
        if (quantity > 0 && item) lines.push({ ...item, quantity, role, stepTitle, subscribable });
      });
    } else {
      // Radios (one product) or checkboxes (Set builder images in add-on steps — several products)
      step.querySelectorAll('input[data-set-option]:checked').forEach((input) => {
        const item = items[/** @type {HTMLInputElement} */ (input).value];
        if (item) lines.push({ ...item, quantity: 1, role, stepTitle, subscribable });
      });
    }

    return lines;
  }

  /** @returns {SetLine[]} */
  #getLines() {
    return this.#steps.flatMap((step) => this.#getStepLines(step));
  }

  get #frequencySelect() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-purchase-frequency]'));
  }

  #isSubscription() {
    const subscription = this.querySelector('[data-purchase-subscription]');
    if (!subscription || subscription.hasAttribute('hidden')) return false;
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-purchase-option]:checked'));
    return checked?.value === 'subscription' && Boolean(this.#frequencySelect?.value);
  }

  /** Steps checked "Use for subscription options"; none checked = every step. */
  get #subscriptionSteps() {
    const steps = this.#steps;
    const checked = steps.filter((step) => step.dataset.subscription === 'true');
    return checked.length ? checked : steps;
  }

  /** @param {HTMLElement} step */
  #isSubscriptionStep(step) {
    return this.#subscriptionSteps.includes(step);
  }

  /**
   * Selling plans are matched across products by frequency ("Delivery every 30 days" = "30 Day(s)"…),
   * falling back to the normalized plan name.
   * @param {SellingPlan} plan
   */
  #planKey(plan) {
    const label = String(plan.name || '')
      .normalize('NFKC')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
    const interval = label.match(/\b(\d+)\s*[- ]?\s*(days?|weeks?|months?|years?)\b/);
    if (interval) return `${Number(interval[1])}:${interval[2].replace(/s$/, '')}`;
    return label;
  }

  /**
   * Frequency options = selling plans shared by all products (with plans) of the subscription steps.
   * @returns {{ key: string, name: string }[]}
   */
  #getFrequencies() {
    const items = this.#subscriptionSteps
      .flatMap((step) => Object.values(this.#getItems(step)))
      .filter((item) => item.plans.length > 0);
    if (!items.length) return [];

    /** @type {Map<string, string>} */
    const common = new Map();
    items[0].plans.forEach((plan) => {
      const key = this.#planKey(plan);
      if (!common.has(key)) common.set(key, plan.name);
    });
    items.slice(1).forEach((item) => {
      const keys = new Set(item.plans.map((plan) => this.#planKey(plan)));
      for (const key of [...common.keys()]) {
        if (!keys.has(key)) common.delete(key);
      }
    });
    return [...common].map(([key, name]) => ({ key, name }));
  }

  #buildFrequencies() {
    const select = this.#frequencySelect;
    const subscription = /** @type {HTMLElement | null} */ (this.querySelector('[data-purchase-subscription]'));
    if (!select || !subscription) return;

    const frequencies = this.#getFrequencies();
    const current = select.value;
    const signature = frequencies.map((frequency) => frequency.key).join('|');

    if (select.dataset.signature !== signature) {
      select.dataset.signature = signature;
      select.replaceChildren(
        ...frequencies.map(({ key, name }) => {
          const option = document.createElement('option');
          option.value = key;
          option.textContent = name;
          option.selected = key === current;
          return option;
        })
      );
    }

    const hasPlans = frequencies.length > 0;
    subscription.hidden = !hasPlans;

    if (!hasPlans) {
      const onetime = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-purchase-option][value="onetime"]'));
      if (onetime) onetime.checked = true;
    }
  }

  /**
   * "/month", "/2 weeks"… from the frequency key ("1:month", "2:week"); empty when the plan has no interval.
   * @param {string} frequency
   */
  #periodSuffix(frequency) {
    const match = String(frequency).match(/^(\d+):(day|week|month|year)$/);
    if (!match) return '';
    const count = Number(match[1]);
    return count === 1 ? `/${match[2]}` : `/${count} ${match[2]}s`;
  }

  /** @param {SetItem} item @param {string} frequency - plan key @returns {SellingPlan | null} */
  #getPlan(item, frequency) {
    if (!item.plans.length || !frequency) return null;
    return item.plans.find((plan) => this.#planKey(plan) === frequency) || null;
  }

  /**
   * Selling plan the line is added with: free pouch — never; subscription steps — on Subscribe, if the product
   * has the chosen frequency; products that can only be sold with a plan — always (first plan as fallback).
   * @param {SetLine} line
   * @param {string} frequency
   * @param {boolean} [forSubscription] - price the Subscribe option (ignores the current choice)
   */
  #planForLine(line, frequency, forSubscription = this.#isSubscription()) {
    if (line.role === 'free' || !line.plans.length) return null;
    if (forSubscription && line.subscribable) {
      const plan = this.#getPlan(line, frequency);
      if (plan) return plan;
    }
    if (line.requiresPlan) return this.#getPlan(line, frequency) || line.plans[0] || null;
    return null;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    return formatMoney(cents, format, currency);
  }

  #refresh() {
    this.#buildFrequencies();
    this.#update();
  }

  #update() {
    const frequency = this.#frequencySelect?.value || '';
    let missingRequired = false;

    for (const step of this.#steps) {
      const lines = this.#getStepLines(step);
      const selected = step.querySelector('[data-step-selected]');

      if (selected) {
        selected.textContent = lines
          .map((line) => line.title)
          .join(', ');
      }

      step.dataset.complete = String(lines.length > 0);
      if (step.dataset.required === 'true' && lines.length === 0) missingRequired = true;
    }

    const lines = this.#getLines();
    const prices = this.#pricing(lines, frequency);
    const percentBadge = prices.percent > 0 ? `-${prices.percent}% off` : '';

    // Badges: % on set / add-on steps
    for (const step of this.#steps) {
      const role = step.dataset.role || 'none';
      const hasSelection = step.dataset.complete === 'true';
      // No badge on the free pouch step — only % on the set and add-ons
      const badgeText = role === 'set' || role === 'addon' ? percentBadge : '';
      const stepBadge = /** @type {HTMLElement | null} */ (step.querySelector('[data-step-discount]'));
      if (stepBadge) {
        stepBadge.textContent = badgeText;
        stepBadge.hidden = !badgeText || !hasSelection;
      }
      step.querySelectorAll('[data-set-row]').forEach((row) => {
        if (!(row instanceof HTMLElement)) return;
        const rowBadge = /** @type {HTMLElement | null} */ (row.querySelector('[data-row-discount]'));
        if (!rowBadge) return;
        rowBadge.textContent = badgeText;
        rowBadge.hidden = !badgeText || !(Number(row.dataset.qty) > 0);
      });
    }

    const setText = (selector, value, hideWhenEmpty = false) => {
      const element = /** @type {HTMLElement | null} */ (this.querySelector(selector));
      if (!element) return;
      element.textContent = value;
      if (hideWhenEmpty) element.hidden = !value;
    };

    // Purchase options show the discounted totals, original price struck through
    setText('[data-subscription-price]', this.#money(prices.subscription));
    setText('[data-subscription-compare]', prices.original > prices.subscription ? this.#money(prices.original) : '');
    setText('[data-onetime-price]', this.#money(prices.onetime));
    setText('[data-onetime-compare]', prices.original > prices.onetime ? this.#money(prices.original) : '', true);

    const recurring = /** @type {HTMLElement | null} */ (this.querySelector('[data-subscription-recurring]'));
    if (recurring) {
      setText('[data-subscription-recurring-price]', prices.recurring > 0 ? this.#money(prices.recurring) : '');
      recurring.hidden = prices.recurring <= 0;
    }
    setText(
      '[data-subscription-period]',
      prices.recurring > 0 ? `${this.#money(prices.recurring)}${this.#periodSuffix(frequency)}` : '',
      true
    );

    this.#renderOptionPrices(prices.percent);

    const finalTotal = this.#isSubscription() ? prices.subscription : prices.onetime;
    setText('[data-set-total]', lines.length ? this.#money(finalTotal) : '');
    setText('[data-set-total-compare]', lines.length && prices.original > finalTotal ? this.#money(prices.original) : '', true);

    this.#renderMilestones({ count: prices.addonCount, money: finalTotal }, lines.length > 0);

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-set-add]'));
    if (button) button.disabled = lines.length === 0 || missingRequired;

    this.#missingRequired = missingRequired;
  }

  #missingRequired = false;

  /**
   * Set builder images: price after the bundle % (set / add-on steps), original (or compare at) price struck through.
   * @param {number} percent
   */
  #renderOptionPrices(percent) {
    for (const step of this.#steps) {
      const role = step.dataset.role || 'none';
      const stepPercent = role === 'set' || role === 'addon' ? percent : 0;
      step.querySelectorAll('[data-option-price]').forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        const price = Number(element.dataset.price) || 0;
        const compareElement = /** @type {HTMLElement | null} */ (element.parentElement?.querySelector('[data-option-compare]'));
        const compareAt = Number(compareElement?.dataset.compare) || 0;

        // Variant picker row: discounted price only while the product is added (same as its % badge);
        // Set builder images: discounted price is always shown
        const row = /** @type {HTMLElement | null} */ (element.closest('[data-set-row]'));
        const showDiscount = row ? Number(row.dataset.qty) > 0 : true;
        const final = showDiscount ? price - Math.round((price * stepPercent) / 100) : price;
        const compare = Math.max(compareAt, final < price ? price : 0);
        element.textContent = this.#money(final);
        if (compareElement) {
          compareElement.textContent = compare > final ? this.#money(compare) : '';
          compareElement.hidden = !(compare > final);
        }
      });
    }
  }

  /**
   * Milestones (Discount milestone blocks), in the order of the blocks:
   * - unit "count": bundle discount tiers (number of add-on pouches), from the Matcha rewards metaobject
   * - unit "money": spend offers handled by Rebuy (free shipping, gifts) — display only, vs the set total
   * Unlocked ones go to the left column, only the next locked one is shown on the right.
   * @param {{ count: number, money: number }} values
   * @param {boolean} hasItems
   */
  #renderMilestones(values, hasItems) {
    const rewards = /** @type {HTMLElement | null} */ (this.querySelector('[data-set-rewards]'));
    const unlockedContainer = /** @type {HTMLElement | null} */ (this.querySelector('[data-set-rewards-unlocked]'));
    const nextContainer = /** @type {HTMLElement | null} */ (this.querySelector('[data-set-rewards-next]'));
    if (!rewards || !unlockedContainer || !nextContainer) return;

    const found = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.js-mix-summary-milestone')]);
    // Remember the block order on first render (elements are moved between columns afterwards)
    found.forEach((element, index) => {
      if (!element.dataset.order) element.dataset.order = String(index + 1);
    });
    const elements = found.sort((a, b) => Number(a.dataset.order || 0) - Number(b.dataset.order || 0));

    const milestones = elements.map((element) => {
      const unit = element.dataset.unit === 'count' ? 'count' : 'money';
      const threshold = Number(element.dataset.threshold) || 0;
      const sameUnitBelow = elements
        .filter((other) => other !== element && (other.dataset.unit === 'count' ? 'count' : 'money') === unit)
        .map((other) => Number(other.dataset.threshold) || 0)
        .filter((value) => value < threshold);
      const value = values[unit];
      return {
        element,
        unit,
        threshold,
        value,
        previousThreshold: sameUnitBelow.length ? Math.max(...sameUnitBelow) : 0,
        isUnlocked: hasItems && threshold > 0 && value >= threshold,
      };
    });

    const unlocked = milestones.filter((milestone) => milestone.isUnlocked);
    const next = milestones.find((milestone) => !milestone.isUnlocked);
    const bestDiscount = unlocked
      .filter((milestone) => milestone.unit === 'count')
      .reduce((best, milestone) => {
        const percent = Number.parseFloat(milestone.element.dataset.tierId?.split(':').pop()) || 0;
        const bestPercent = Number.parseFloat(best?.element.dataset.tierId?.split(':').pop()) || 0;
        return !best || percent > bestPercent ? milestone : best;
      }, null);
    const visibleUnlocked = unlocked.filter((milestone) => milestone.unit !== 'count' || milestone === bestDiscount);
    const latest = visibleUnlocked[visibleUnlocked.length - 1];

    for (const milestone of milestones) {
      const { element, unit, threshold, value, previousThreshold, isUnlocked } = milestone;
      const text = element.querySelector('.js-mix-summary-milestone-text');
      const progress = /** @type {HTMLProgressElement | null} */ (element.querySelector('.js-mix-summary-progress'));
      const left = Math.max(0, threshold - value);
      const remaining = unit === 'count' ? String(left) : this.#money(left);
      const label = ((isUnlocked ? element.dataset.unlockedText : element.dataset.progressText) || '').replaceAll(
        '[amount]',
        remaining
      );

      element.hidden = isUnlocked
        ? unit === 'count' && milestone !== bestDiscount
        : milestone !== next;
      element.classList.toggle('is-latest', milestone === latest);
      if (text) text.textContent = label;
      if (progress) {
        const intervalProgress = ((value - previousThreshold) / Math.max(1, threshold - previousThreshold)) * 100;
        progress.value = isUnlocked ? 100 : Math.min(100, Math.max(0, intervalProgress));
        progress.setAttribute('aria-label', label);
      }

      // Move only when the column changes — avoids endless MutationObserver loops
      const target = isUnlocked ? unlockedContainer : nextContainer;
      if (element.parentElement !== target) target.append(element);
    }

    if (latest && unlockedContainer.lastElementChild !== latest.element) {
      unlockedContainer.append(latest.element);
    }
    unlockedContainer.hidden = unlocked.length === 0;
    nextContainer.hidden = !next;
    rewards.hidden = milestones.length === 0;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const toggle = target.closest('[data-step-toggle]');
    if (toggle) {
      const step = /** @type {HTMLElement | null} */ (toggle.closest('[data-set-step]'));
      if (!step) return;
      const open = step.dataset.open !== 'true';
      step.dataset.open = String(open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const row = /** @type {HTMLElement | null} */ (target.closest('[data-set-row]'));
    if (row) {
      const quantity = Number(row.dataset.qty) || 0;
      if (target.closest('[data-row-add]')) this.#setRowQuantity(row, 1);
      else if (target.closest('[data-row-plus]')) this.#setRowQuantity(row, quantity + 1);
      else if (target.closest('[data-row-minus]')) this.#setRowQuantity(row, quantity - 1);
      return;
    }

    if (target.closest('[data-set-add]')) this.#addToCart();
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-set-option], [data-purchase-option], [data-purchase-frequency]')) this.#update();
  };

  /** @param {HTMLElement} row @param {number} quantity */
  #setRowQuantity(row, quantity) {
    const value = Math.max(0, quantity);
    const hadFocusInQty = row.querySelector('[data-row-minus]') === document.activeElement;

    row.dataset.qty = String(value);
    const qty = row.querySelector('[data-row-qty]');
    if (qty) qty.textContent = String(value);

    // Keep keyboard focus on the visible control
    if (value === 1 && document.activeElement?.closest('[data-row-add]')) {
      /** @type {HTMLElement | null} */ (row.querySelector('[data-row-plus]'))?.focus();
    } else if (value === 0 && hadFocusInQty) {
      /** @type {HTMLElement | null} */ (row.querySelector('[data-row-add]'))?.focus();
    }

    this.#update();
  }

  #showError(message = '') {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-set-error]'));
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  async #addToCart() {
    if (this.#missingRequired) {
      this.#showError(this.dataset.errorRequired);
      return;
    }

    const lines = this.#getLines();
    if (!lines.length) return;

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-set-add]'));
    const isSubscription = this.#isSubscription();
    const frequency = this.#frequencySelect?.value || '';

    // All lines of one add-to-cart share `_bundle_id`: the discount function recalculates the bundle on every
    // cart change (removing the set makes the free pouch paid, removing add-ons lowers the %).
    const bundleId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    const setTitles = lines.filter((line) => line.role === 'set').map((line) => line.title).join(', ');
    const freeWithLabel = this.dataset.freeWithLabel || 'Free with';

    const items = lines.map((line) => {
      /** @type {Record<string, string>} */
      const properties = { _bundle: this.#program, _bundle_id: bundleId };
      if (line.role !== 'none') properties._bundle_role = line.role;
      if (line.role === 'free' && setTitles) properties[freeWithLabel] = setTitles;

      /** @type {{ id: number, quantity: number, properties: Record<string, string>, selling_plan?: number }} */
      const item = { id: line.id, quantity: line.quantity, properties };
      // Free pouch stays one-time; products of the subscription steps follow Subscribe / One-time
      const plan = this.#planForLine(line, frequency, isSubscription);
      if (plan) item.selling_plan = plan.id;
      return item;
    });

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    this.#showError();
    button?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((res) => res.json());

      const cart = await this.#fetchCart();
      const didError = Boolean(response.status);

      if (didError) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        this.#showError(response.description || response.message);
      }

      // Update header cart counter directly — doesn't depend on event listeners timing
      this.#updateCartBubble(cart.item_count);

      /** @type {any} */
      let cartSummary = null;
      try {
        cartSummary = CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
      } catch (error) {
        console.warn('[set-builder] Could not convert cart', error);
      }

      deferred.resolve({
        cart: cartSummary,
        detail: {
          items: cart.items,
          source: 'set-builder',
          sourceId: this.id || 'set-builder',
          itemCount: cart.item_count,
          productId: this.dataset.productId,
          ...(didError ? {} : { sections: response.sections }),
          didError,
        },
      });

      if (!didError && button) {
        button.setAttribute('data-added', '');
        setTimeout(() => button.removeAttribute('data-added'), ADDED_STATE_DURATION);
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  /**
   * Always fetch a fresh cart after the add request (cart-items-component may return an in-flight, stale fetch).
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js?t=${Date.now()}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {number} count */
  #updateCartBubble(count) {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      const cartIcon = /** @type {any} */ (icon);
      if (typeof cartIcon.renderCartBubble === 'function') cartIcon.renderCartBubble(count);
    });
  }
}

if (!customElements.get('set-builder')) customElements.define('set-builder', SetBuilder);
